import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type CartDrawer } from '@/scripts/interactive-defer/cart-drawer'
import { type ModalDialog } from '@/scripts/interactive-defer/modal-dialog'
import type {
	CartItem,
	Cart,
	CartErrorResponse,
	ProductVariant,
} from '@/scripts/shopify'

type SectionsResponse = Record<string, string>

type CartAdd = {
	items: CartItem[]
}

export type CartAddWithSections = CartAdd & {
	sections: SectionsResponse
}

export type CartUpdateWithSections = Cart & {
	sections: SectionsResponse
}

export async function getCart(): Promise<Cart | CartErrorResponse> {
	try {
		const cart = await fetch('/cart.js', {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		}).then((response) => response.json())
		if (cart.status) {
			return cart as CartErrorResponse
		}
		return cart as Cart
	} catch (error) {
		console.error(error)
		return {
			status: 500,
			description: 'Error in HTTP request',
			message:
				'This cart operation failed, but Shopify did not return an error',
		}
	}
}

export function getDOMCart() {
	return q.os<CartDrawer>('cart-drawer')
}

export function hasDomCart() {
	return getDOMCart() !== undefined
}

export function getDOMCartSectionApiIds() {
	const cartEl = getDOMCart()
	if (!cartEl) return undefined
	return [
		'cart-drawer-items',
		'cart-icon-bubble',
		'dynamic-shipping-bar',
		'dynamic-cart-footer',
		'dynamic-cart-recommendations',
	]
}

const ignoredProperties = ['utf8', 'product-id', 'section-id']

export function updateCartRecs(rawHTML: string) {
	const newDocument = new DOMParser().parseFromString(rawHTML, 'text/html')
	const recs =
		q.rs('[data-has-recs]', newDocument).getAttribute('data-has-recs') ??
		'true'
	q
		.ol('[data-has-recs]')
		?.forEach((el) => el.setAttribute('data-has-recs', recs))

	renderRawHTMLToDOM({
		sourceHTML: rawHTML,
		sourceSelector: '[data-dynamic-cart-recommendations]',
		destinationSelector: '[data-dynamic-cart-recommendations="desktop"]',
		destinationSelectorContainer: `#CartDrawer`,
	})
}

export function renderResponseToCartDrawer(
	cart: CartAddWithSections | CartUpdateWithSections | CartAdd,
	modal?: ModalDialog
) {
	if (modal) {
		document.body.addEventListener(
			'modalClosed',
			() => {
				setTimeout(() => {
					renderResponseToCartDrawer(cart)
				})
			},
			{ once: true }
		)
		modal.hide(true)
		return
	}

	const cartEl = getDOMCart()

	if (!cartEl) {
		window.location.href = window.routes.cart_url
		return
	}

	if ('sections' in cart) {
		cartEl.renderContents(cart)
	}
}

function createAddToCartInputFromFormData(
	formData: FormData,
	sections?: string[]
): AddToCartInput {
	const properties: Record<string, string> = {}
	let id: number | undefined = undefined
	let quantity: number | undefined = undefined
	let selling_plan: number | undefined = undefined
	let form_type: string | undefined = undefined
	for (let [key, value] of formData.entries()) {
		if (value instanceof File) continue
		switch (key) {
			case 'id':
				id = parseInt(value)
				break
			case 'quantity':
				quantity = parseInt(value)
				break
			case 'form_type':
				form_type = value
				break
			case 'selling_plan':
				selling_plan = parseInt(value)
				break
			default:
				if (
					!ignoredProperties.includes(key) &&
					!key.includes('options[')
				) {
					properties[key] = value.toString()
				}
		}
	}
	if (!id) throw Error('No variant id found')
	if (!quantity) quantity = 1
	if (!form_type) throw Error('No form type found')
	const item: AddToCartItem = {
		id,
		quantity,
		properties,
	}

	if (selling_plan) {
		item.selling_plan = selling_plan
	}

	if (sections && sections.length) {
		return {
			items: [item],
			form_type,
			sections,
		}
	} else {
		return {
			items: [item],
			form_type,
		}
	}
}

type AddToCartItem = {
	id: number
	quantity: number
	selling_plan?: number
	properties: Record<string, string>
}

type AddToCartInput = {
	items: AddToCartItem[]
	form_type: string
	sections?: string[]
	sections_url?: string
}

type RenderRawHTMLToDOMInput = {
	sourceHTML: string
	sourceSelector?: string
	destinationSelectorContainer?: string
	destinationSelector: string
}

export function renderRawHTMLToDOM({
	sourceHTML,
	sourceSelector,
	destinationSelector,
	destinationSelectorContainer,
}: RenderRawHTMLToDOMInput) {
	const sourceSelectorOrDefault = sourceSelector ?? '.shopify-section'
	const newDocument = new DOMParser().parseFromString(sourceHTML, 'text/html')
	const sourceElement = q.rs(
		sourceSelectorOrDefault,
		newDocument.documentElement
	)
	if (destinationSelectorContainer) {
		const container = q.rs(destinationSelectorContainer)
		const destinationElement = q.rs(destinationSelector, container)
		destinationElement.outerHTML = sourceElement.outerHTML
	} else {
		const destinationElement = q.rs(destinationSelector)
		destinationElement.outerHTML = sourceElement.outerHTML
	}
}

export function configPostRequest(data: unknown) {
	return {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: `application/json`,
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: JSON.stringify(data),
	}
}

export async function addItemsToCart(
	input: AddToCartItem[] | FormData,
	sections?: string[]
): Promise<CartAdd | CartAddWithSections | CartErrorResponse> {
	// TO
	const data =
		input instanceof FormData
			? createAddToCartInputFromFormData(input, sections)
			: sections && sections.length
				? {
						items: input,
						sections: sections,
					}
				: { items: input }
	try {
		const response = await fetch(
			`${window.routes.cart_add_url}`,
			configPostRequest(data)
		)
		const cart = await response.json()
		if (cart.status) {
			return cart as CartErrorResponse
		}
		if (sections) {
			return cart as CartAddWithSections
		} else {
			return cart as CartAdd
		}
	} catch (error) {
		console.error(error)
		return {
			status: 500,
			description: 'Error in HTTP request',
			message:
				'This cart operation failed, but Shopify did not return an error',
		}
	}
}

type CartUpdateByKeyItem = {
	key: string
	quantity: number
}

type CartUpdateByIdItem = {
	id: number
	quantity: number
}

export async function updateCartRequest(
	newItems: CartUpdateByIdItem[],
	existingItems: CartUpdateByKeyItem[],
	sections?: string[]
) {
	const updates: Record<number | string, number> = {}
	newItems.forEach((item) => {
		updates[item.id] = item.quantity
	})
	existingItems.forEach((item) => {
		updates[item.key] = item.quantity
	})
	const data =
		sections && sections.length
			? {
					updates,
					sections,
				}
			: {
					updates,
				}

	try {
		const response = await fetch(
			`${window.routes.cart_update_url}`,
			configPostRequest(data)
		)
		const cart = await response.json()
		if (cart.status) {
			return cart as CartErrorResponse
		}
		return cart as Cart
	} catch (error) {
		console.error(error)
		return {
			status: 500,
			description: 'Error in HTTP request',
			message:
				'This cart operation failed, but Shopify did not return an error',
		}
	}
}

export type ShopifyProductOptions = {
	option1: string
	option2?: string
	option3?: string
}

export function variantFromProductOptions(
	options: ShopifyProductOptions,
	variants: ProductVariant[]
): ProductVariant {
	const variant = variants.find((variant) => {
		if (variant.option1 !== options.option1) return false
		if (options.option2 && variant.option2 !== options.option2) return false
		if (options.option3 && variant.option3 !== options.option3) return false
		return true
	})
	if (!variant) throw Error('No variant found')
	return variant
}

export async function getSectionHTMLForResource(
	url: string,
	sectionId: string
): Promise<string | undefined> {
	let path = `${url}?sections=${sectionId}`
	if (url.includes('?')) {
		path = `${url}&sections=${sectionId}`
	}
	try {
		return await fetch(path)
			.then((response) => {
				return response.json()
			})
			.then((response) => {
				const html = response[sectionId]
				const parser = new DOMParser()
				const doc = parser.parseFromString(html, 'text/html')
				return q.rs('.shopify-section', doc).innerHTML
			})
	} catch (e) {
		console.error('error in getSectionHTMLForResource')
		console.log(e)
	}
}

export function getActiveOrAccessibilityElement(): HTMLElement {
	const activeElement = document.activeElement
	if (activeElement instanceof HTMLElement) {
		return activeElement
	} else {
		return q.rs('[data-uc-accessibility-focus]')
	}
}

export function toggleHeaderColorScheme(
	scheme: 'starting' | 'menu',
	tempTransparent: 'none' | 'on' | 'off' = 'none'
) {
	const elements = q.rl('[data-color-scheme-group="header"]')

	if (scheme === 'starting') {
		elements.forEach((el) => {
			const scheme = q.ra(el, 'data-starting-color-scheme')
			el.setAttribute('data-color-scheme', scheme)
			if (tempTransparent === 'on') {
				el.style.background = 'transparent'
			} else {
				el.removeAttribute('style')
			}
		})
	}
	if (scheme === 'menu') {
		elements.forEach((el) => {
			const scheme = q.ra(el, 'data-menu-color-scheme')
			el.setAttribute('data-color-scheme', scheme)
			if (tempTransparent === 'on') {
				el.style.background = 'transparent'
			} else {
				el.removeAttribute('style')
			}
		})
	}
}

export function openCartIfRedirect() {
	const params = new URLSearchParams(window.location.search)
	if (params.has('open-cart')) {
		const cartEl = getDOMCart()
		if (cartEl) {
			cartEl.open()
		}
	}
}

export function setCustomerCategory() {
	const params = new URLSearchParams(window.location.search)
	if (params.has('customer-category')) {
		// use shopify cart ajax api to update the cart with this category with a fetch()
		const category = params.get('customer-category')
		if (!category) return
		void fetch('/cart/update.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				attributes: {
					_customer_category: category,
				},
			}),
		})
		const checkboxes = {
			womens: q.os('input[name="customer_category_womens"]'),
			mens: q.os('input[name="customer_category_mens"]'),
			bridal: q.os('input[name="customer_category_bridal"]'),
		}
		/// iterate checkboxes object with keys and values
		Object.entries(checkboxes).forEach(([key, value]) => {
			if (value instanceof HTMLInputElement) {
				value.checked = category === key
			}
		})
		setPrimaryLinkForCustomerCategory(category)
	}
}

function setPrimaryLinkForCustomerCategory(category: string) {
	const primaryLink = q.os('[data-primary-link]')
	const categoryLink = primaryLink?.getAttribute(`data-${category}-lp`)
	if (primaryLink && categoryLink) {
		primaryLink.setAttribute('href', categoryLink)
	}
}

export function updateFromCartCategoryCookie() {
	const params = new URLSearchParams(window.location.search)
	if (params.has('customer-category')) return
	const category = q.os('#customerCategory')?.getAttribute('data-category')
	if (!category || category === 'none') return
	setPrimaryLinkForCustomerCategory(category)
}
